/**
 * The template list.
 *
 * The column that matters is the version state. A template with a draft and no
 * published version cannot be sent at all, and a campaign picker will not offer
 * it — so "v2 · published, v3 draft" is not decoration, it is the answer to
 * "why can I not select this?".
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api, ApiError, emptyPage } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type { Channel, CursorPage, Template } from "../api/types";

const PAGE_SIZE = 50;

export function Templates() {
  const { orgId, can } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Cursors are forward-only, so going back means remembering where each page
  // started rather than computing an offset.
  const [history, setHistory] = useState<string[]>([]);
  const cursor = history.at(-1) ?? "";

  const search = params.get("q") ?? "";

  const { data, error, loading, reload } = useAsync<CursorPage<Template>>(
    (signal) =>
      api.get<CursorPage<Template>>("/v1/templates", {
        query: { cursor, limit: PAGE_SIZE },
        signal,
      }),
    [orgId, cursor],
  );

  const page = data ?? emptyPage<Template>();

  // Filtered here rather than in a query parameter: the list endpoint takes no
  // search term, and inventing one client-side that only searches the page you
  // are looking at would be a worse lie than filtering visibly.
  const needle = search.trim().toLowerCase();
  const rows = needle
    ? page.data.filter((t) => t.name.toLowerCase().includes(needle))
    : page.data;

  const firstIndex = history.length * PAGE_SIZE + 1;

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Templates</h1>
        <div className="row" style={{ gap: "var(--oc-space-3)" }}>
          <div className="oc-search" style={{ width: 280 }}>
            <Icon name="search" className="oc-search__icon" />
            <input
              className="oc-input"
              data-screen-search
              placeholder="Filter this page by name…"
              defaultValue={search}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                const value = event.currentTarget.value.trim();
                setParams(
                  value ? new URLSearchParams({ q: value }) : new URLSearchParams(),
                  { replace: true },
                );
              }}
            />
          </div>
          {can("templates:write") && <NewTemplate onCreated={(id) => navigate(`/templates/${id}`)} />}
        </div>
      </div>

      <div className="content">
        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table templates-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Name</span>
              <span className="oc-table__cell--center">Channel</span>
              <span>Version</span>
              <span className="oc-table__cell--right">Created</span>
            </div>

            <div className="oc-table__body">
              {error && <Failed detail={error.message} onRetry={reload} />}
              {loading && rows.length === 0 && <SkeletonRows columns={4} />}

              {!error && rows.length === 0 && !loading && (
                <NoTemplates filtered={Boolean(needle)} />
              )}
              {rows.map((template) => (
                <Row
                  key={template.id}
                  template={template}
                  onOpen={() => navigate(`/templates/${template.id}`)}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">{summary(rows.length, firstIndex)}</span>
              <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={history.length === 0}
                  onClick={() => setHistory((h) => h.slice(0, -1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={!page.has_more || !page.next_cursor}
                  onClick={() => {
                    if (page.next_cursor) setHistory((h) => [...h, page.next_cursor!]);
                  }}
                >
                  Next
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ template, onOpen }: { template: Template; onOpen: () => void }) {
  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-truncate">
        <span style={{ fontWeight: 500 }}>{template.name}</span>
        {template.description && (
          <span className="oc-body-sm oc-text-secondary oc-truncate" style={{ display: "block" }}>
            {template.description}
          </span>
        )}
      </span>
      <span className="oc-table__cell--center">
        <Icon name={channelIcon(template.channel)} className="channel-icon" />
      </span>
      <span className="oc-mono-sm">
        <VersionState template={template} />
      </span>
      <span className="oc-table__cell--right oc-mono-sm oc-text-secondary">
        {shortDate(template.created_at)}
      </span>
    </div>
  );
}

/**
 * What a send would resolve to, and what is still being worked on.
 *
 * Both halves are shown because they answer different questions. The published
 * version is what a campaign can pin; the draft is what publishing next would
 * freeze.
 */
function VersionState({ template }: { template: Template }) {
  const published = template.current_version;
  const draft = template.draft;

  if (!published && !draft) return <span className="oc-text-muted">no versions</span>;

  return (
    <span className="row" style={{ gap: "var(--oc-space-2)" }}>
      {published ? (
        <span className="oc-text-success">v{published.version} published</span>
      ) : (
        <span className="oc-text-warning">unpublished</span>
      )}
      {draft && <span className="oc-text-secondary">v{draft.version} draft</span>}
    </span>
  );
}

function NewTemplate({ onCreated }: { onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Template>("/v1/templates", { name: name.trim() });
      setName("");
      setOpen(false);
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the template.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="oc-btn oc-btn--primary" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} />
        New template
      </button>
    );
  }

  return (
    <form className="row" style={{ gap: "var(--oc-space-2)" }} onSubmit={(event) => void submit(event)}>
      <input
        className="oc-input"
        style={{ width: 200 }}
        // Focused on open: the button was the affordance, and making someone
        // click twice to start typing is the kind of friction nobody reports.
        autoFocus
        placeholder="welcome-email"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      />
      <button type="submit" className="oc-btn oc-btn--primary" disabled={busy || !name.trim()}>
        {busy ? "Creating…" : "Create"}
      </button>
      <button type="button" className="oc-btn oc-btn--ghost" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <p className="field-error">{error}</p>}
    </form>
  );
}

// Same split as NoMessages and NoContacts: the curl goes to whoever can mint
// the key it uses. This one already said "Create one above" — the New Template
// control is right there — so for a marketing member the API half was not just
// unusable, it was competing with the button that works.
function NoTemplates({ filtered }: { filtered: boolean }) {
  const { can } = useSession();

  if (filtered) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No template on this page matches</div>
        <div className="oc-empty__hint">
          The filter applies to the page you are looking at, not the whole list. Clear it, or page
          on.
        </div>
      </div>
    );
  }

  if (!can("keys:manage")) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No templates yet</div>
        <div className="oc-empty__hint">
          A template holds the subject and body a campaign sends. Each one is versioned, so the
          message log can always show what was actually delivered.
          {can("templates:write") && " Create your first one above."}
        </div>
      </div>
    );
  }

  return (
    <div className="oc-empty">
      <div className="oc-empty__title">No templates yet</div>
      <div className="oc-empty__hint">
        A template holds the subject and body a campaign sends, versioned so the log can show what
        was actually delivered. Create one above, or from the API:
      </div>
      <pre className="oc-code">{`curl -X POST ${window.location.origin}/v1/templates \\
  -H "Authorization: Bearer $OPENCOMMS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"welcome-email"}'`}</pre>
    </div>
  );
}

function Failed({ detail, onRetry }: { detail: string; onRetry: () => void }) {
  return (
    <div className="oc-empty">
      <div className="oc-empty__title">Could not load templates</div>
      <div className="oc-empty__hint">{detail}</div>
      <button type="button" className="oc-btn oc-btn--sm" onClick={onRetry}>
        <Icon name="refresh" size={12} />
        Try again
      </button>
    </div>
  );
}

/** Count only. `estimated_total` is never populated for templates. */
function summary(count: number, firstIndex: number): string {
  if (count === 0) return "No templates";
  return `${firstIndex}–${firstIndex + count - 1}`;
}

export function channelIcon(channel: Channel): "mail" | "sms" | "push" | "campaign" {
  switch (channel) {
    case "sms":
      return "sms";
    case "push":
      return "push";
    case "email":
      return "mail";
    default:
      return "campaign";
  }
}

export function shortDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
